<script setup>
import moment from 'moment'
import { cookieRef } from '@/@layouts/stores/config'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'
import QRCode from '@chenfengyuan/vue-qrcode'
import CryptoJS from 'crypto-js'
import KioskDialog from '@/views/care-visit/kiosk/KioskDialog.vue'
import KioskRevokeAccessDialog from '@/views/care-visit/kiosk/KioskRevokeAccessDialog.vue'
import KioskService from '@/services/care-visit/kiosk.service'
import { useTenantStore } from '@/stores/tenant'

definePage({
  meta: {
    name: "Kiosk",
    subject: "care_visit.menu",
    action: "kiosk",
  },
})

const unitId = computed(() => cookieRef('selectedUnit').value ?? null)

const headers = ref([
  {
    title: 'Kiosk',
    key: 'name',
  },
  {
    title: 'Location',
    key: 'visitor_location',
  },
  {
    title: 'Last Heartbeat',
    key: 'last_heartbeat_at',
  },
  {
    title: 'Type',
    key: 'kiosk_type',
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
  },
])

const filter = computed(() => {
  return {
    unit_id: unitId.value,
  }
})

const searchQuery = ref('')
const itemsPerPage = ref(10)
const pageNumber = ref(1)
const sortBy = ref()
const orderBy = ref()

const {
  isFetching,
  data: kiosksData,
  execute: fetchKiosks,
} = useApi(createUrl(`/care-visit/kiosks`, {
  query: {
    paged: 1,
    q: searchQuery,
    search: filter,
    itemsPerPage: itemsPerPage,
    page: pageNumber,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const kiosks = computed(() => kiosksData.value?.data ?? kiosksData.value)
const totalKiosks = computed(() => kiosksData.value?.meta?.total ?? kiosksData.value?.length)

const isKioskDialogOpen = ref(false)
const kiosk = ref()

const isQrCodeDialogOpen = ref(false)
const selectedKioskForQr = ref(null)

const isRevokeAccessDialogOpen = ref(false)
const kioskForRevoke = ref(null)

const openRevokeAccessDialog = item => {
  kioskForRevoke.value = item
  isRevokeAccessDialogOpen.value = true
}

const qrCodeValue = computed(() => {
  if (!selectedKioskForQr.value) return ''

  const data = JSON.stringify({
    tenant_base_url: useTenantStore().apiUrl,
    location_code: selectedKioskForQr.value?.visitor_location?.code ?? '',
    serial_number: selectedKioskForQr.value?.serial_number ?? '',
  })

  const encryptionKey = import.meta.env.VITE_ENCRYPTION_KEY

  return CryptoJS.AES.encrypt(data, encryptionKey).toString()
})

const openQrCodeDialog = item => {
  selectedKioskForQr.value = item
  isQrCodeDialogOpen.value = true
}

const editKiosk = item => {
  kiosk.value = item
  isKioskDialogOpen.value = true
}

watch(() => isKioskDialogOpen.value, value => {
  if (!value) kiosk.value = null
})

const deleteKiosk = item => {
  if (confirm('Are you sure you want to delete this kiosk?')) {
    KioskService.delete(
      item.id,
      () => {
        fetchKiosks()
      },
      () => {
        //
      },
    )
  }
}

const getStatusColor = status => {
  const colors = {
    'active': 'success',
    'online': 'success',
    'offline': 'error',
    'inactive': 'secondary',
    'maintenance': 'warning',
    'error': 'error',
  }

  return colors[status] || 'secondary'
}

const formatKioskType = type => {
  const types = {
    'check-in': 'Check-in',
    'check-out': 'Check-out',
    'both': 'Both',
  }

  return types[type] || type
}
</script>

<template>
  <VCard
    variant="flat"
    class="border"
  >
    <VCardText class="d-flex flex-wrap py-4 gap-4">
      <div
        class="me-3 d-flex gap-3"
        style="inline-size: 24rem;"
      >
        <!-- -->
      </div>
      <VSpacer />

      <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
        <!-- Search -->
        <div style="inline-size: 12rem;">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>
        <VBtn
          variant="flat"
          prepend-icon="tabler-plus"
          :disabled="!$can('create', 'care_visit.kiosk')"
          @click="isKioskDialogOpen = true"
        >
          Add Kiosk
        </VBtn>

        <IconBtn
          variant="tonal"
          class="rounded"
          :disabled="isFetching"
          @click="fetchKiosks"
        >
          <VIcon icon="tabler-refresh" />
        </IconBtn>
      </div>
    </VCardText>

    <VDivider />

    <VDataTableServer
      v-model:items-per-page="itemsPerPage"
      v-model:page="pageNumber"
      :items-length="totalKiosks"
      :items="kiosks ?? []"
      :headers="headers"
      fixed-header
      class="text-no-wrap"
      :loading="isFetching"
    >
      <template #item.name="{ item }">
        <div class="d-flex align-center">
          <VAvatar
            :color="getStatusColor(item.status)"
            size="32"
            class="me-3"
          >
            <VIcon
              icon="tabler-device-desktop"
              size="16"
            />
          </VAvatar>
          <div>
            <p class="text-body-2 font-weight-medium mb-0">
              {{ item.name }}
            </p>
            <p
              v-if="item.serial_number"
              class="text-caption text-medium-emphasis mb-0"
            >
              {{ item.serial_number }}
            </p>
          </div>
        </div>
      </template>
      <template #item.visitor_location="{ item }">
        {{ item?.visitor_location?.name ?? 'N/A' }}
      </template>
      <template #item.last_heartbeat_at="{ item }">
        {{ item.last_heartbeat_at ? moment(item.last_heartbeat_at).format('YYYY-MM-DD HH:mm:ss') : 'N/A' }}
      </template>
      <template #item.kiosk_type="{ item }">
        <VChip
          v-if="item?.kiosk_type"
          size="small"
          variant="tonal"
          color="info"
        >
          {{ formatKioskType(item.kiosk_type) }}
        </VChip>
        <span v-else>N/A</span>
      </template>
      <template #item.status="{ item }">
        <VChip
          :color="getStatusColor(item?.status)"
          size="small"
        >
          {{ item?.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : 'Unknown' }}
        </VChip>
      </template>
      <template #item.actions="{ item }">
        <IconBtn @click="openQrCodeDialog(item)">
          <VIcon icon="tabler-qrcode" />
        </IconBtn>
        <IconBtn
          :disabled="!$can('update', 'care_visit.kiosk')"
          @click="editKiosk(item)"
        >
          <VIcon icon="tabler-edit" />
        </IconBtn>
        <IconBtn
          color="error"
          :disabled="!$can('delete', 'care_visit.kiosk')"
          @click="openRevokeAccessDialog(item)"
        >
          <VIcon icon="tabler-lock-off" />
          <VTooltip
            activator="parent"
            location="top"
          >
            Revoke Access
          </VTooltip>
        </IconBtn>
        <IconBtn
          :disabled="!$can('delete', 'care_visit.kiosk')"
          @click="deleteKiosk(item)"
        >
          <VIcon icon="tabler-trash" />
        </IconBtn>
      </template>
    </VDataTableServer>
  </VCard>

  <KioskDialog
    v-model:isDialogOpen="isKioskDialogOpen"
    :form-data="kiosk"
    @saved="fetchKiosks"
  />

  <KioskRevokeAccessDialog
    v-model:isDialogOpen="isRevokeAccessDialogOpen"
    :kiosk="kioskForRevoke"
    @revoked="fetchKiosks"
  />

  <!-- QR Code Dialog -->
  <VDialog
    v-model="isQrCodeDialogOpen"
    max-width="400"
  >
    <DialogCloseBtn
      size="small"
      @click="isQrCodeDialogOpen = false"
    />

    <VCard>
      <VCardText>
        <div class="text-h5">
          Kiosk QR Code
        </div>
        <span>Scan this QR code to configure the kiosk</span>
      </VCardText>

      <VDivider />

      <VCardText class="text-center py-6">
        <div class="mb-4">
          <p class="text-body-1 font-weight-medium mb-1">
            {{ selectedKioskForQr?.name }}
          </p>
          <p class="text-caption text-medium-emphasis">
            {{ selectedKioskForQr?.serial_number }}
          </p>
        </div>
        <QRCode
          v-if="qrCodeValue"
          :value="qrCodeValue"
          :options="{
            errorCorrectionLevel: 'M',
            width: 250,
          }"
        />
      </VCardText>

      <VDivider />

      <VCardText>
        <VAlert
          variant="tonal"
          density="compact"
          border="start"
          color="warning"
        >
          Don't share this QR code with unauthorized personnel. It contains sensitive kiosk configuration data.
        </VAlert>
      </VCardText>
    </VCard>
  </VDialog>
</template>
